import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wrench, Loader2, ShieldCheck, Clock, FileCheck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-gradient-to-br from-teal-700 via-emerald-800 to-sky-900 p-12 lg:flex">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/33623771/pexels-photo-33623771.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[var(--sipkd-primary)]">
            <Wrench className="h-6 w-6 text-white" />
          </div>
          <div>
            <p className="font-display text-lg font-bold leading-tight text-white">SIPKD</p>
            <p className="mt-1 text-xs leading-snug text-teal-50/70">Sistem Pelaporan Kerusakan Kendaraan Dinas</p>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-white md:text-5xl">
            Kelola perbaikan kendaraan dinas secara transparan & terlacak.
          </h1>
          <p className="mt-4 max-w-md text-sm text-teal-50/80">
            Dari pelaporan kerusakan, persetujuan divisi, hingga tindak lanjut bengkel dan laporan akhir resmi — semua dalam satu alur kerja.
          </p>
          <div className="mt-8 flex gap-6">
            {[
              { icon: FileCheck, t: "Alur Bertingkat" },
              { icon: Clock, t: "Riwayat Terlacak" },
              { icon: ShieldCheck, t: "Berbasis Peran" },
            ].map((f) => (
              <div key={f.t} className="flex items-center gap-2 text-teal-50">
                <f.icon className="h-4 w-4 text-teal-300" />
                <span className="text-xs font-medium">{f.t}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-teal-50/60">© 2026 SIPKD Internal System</p>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[var(--sipkd-primary)]">
              <Wrench className="h-5 w-5 text-white" />
            </div>
            <p className="font-display text-lg font-bold text-slate-900">SIPKD</p>
          </div>
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Masuk</h2>
          <p className="mt-1 text-sm text-slate-500">
            Silakan masuk menggunakan akun yang telah didaftarkan.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                placeholder="nama@instansi.go.id"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Kata Sandi</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && (
              <p data-testid="login-error" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-600">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full rounded-full bg-[var(--sipkd-primary)] py-6 text-base text-white transition-colors duration-300 hover:bg-[var(--sipkd-primary-hover)]"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Masuk
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
